import { memo } from 'react';
import { Box, Text, Flex, Skeleton } from '@chakra-ui/react';
import type { WidgetConfig } from './types';
import { RANKING_METRICS as RANKING_METRICS_CATALOG, TIMESERIES_VOLUME_METRICS as TS_VOL_CATALOG, TIMESERIES_MARGIN_METRICS as TS_MARGIN_CATALOG, supportsMarketplaceBreakdown, widgetChartLayoutKey } from './types';
import { TimeseriesChart, PartTypePieChart } from '../components';
import RankingBarChart, {
    VOLUME_METRIC_OPTIONS,
    MARGIN_METRIC_OPTIONS,
    PART_TYPE_METRIC_OPTIONS,
} from '../components/RankingBarChart';
import RankingTableWidget from './RankingTableWidget';
import type { MetricOption as TimeseriesMetricOption, OverviewMetric } from '../components/TimeseriesChart';
import type { MetricOption as RankingMetricOption } from '../components/RankingBarChart';
import { NEGATIVE_METRICS } from '../../../types/reporting';
import { useWidgetData, type WidgetSharedParams } from './useWidgetData';
import { REPORTING_OVERVIEW_BUNDLE_LIMIT } from '../../../hooks/reporting/reportingBundleDomainStats';
import { getPartTypeLabel } from '../../../utils/chartUtils';
import { TbBox, TbCurrencyDollar, TbPercentage } from 'react-icons/tb';
import { Icon } from '@chakra-ui/react';
import { getSummaryComparisonLabel } from '../shared/reportingComparisonLabels';
import {
    formatSummaryMetricChange,
    summaryChangeArrow,
    summaryChangeColor,
} from '../shared/summaryChangeDisplay';
import { isReportingComparisonSupported } from '../shared/reportingComparisonUtils';
import { useDomainDrilldownOrderIds, type DomainDrilldownDomain } from '../../../hooks/reporting/useDomainDrilldownOrderIds';

const ORDER_VOLUME_METRICS: TimeseriesMetricOption[] = [
    { value: 'orderVolume', label: 'Order Volume', format: 'currency', tooltip: 'Total dollars of parts ordered' },
    { value: 'orderVolumeWoPM', label: 'Order Volume (w/o PM)', format: 'currency', tooltip: 'Total list dollars using base listPrice instead of adjustedListPrice from price matching' },
    { value: 'orderCost', label: 'Order Cost', format: 'currency', tooltip: 'Total dollars paid to suppliers for parts ordered' },
    { value: 'orderCount', label: 'Order Count', format: 'number', tooltip: 'Total number of orders' },
    { value: 'avgOrderSize', label: 'Avg. Order Size', format: 'currency', tooltip: 'Total dollars of parts divided by total number of parts ordered' },
    { value: 'medianOrderSize', label: 'Median Order Size', format: 'currency', tooltip: "Median of each's order total dollars of parts" },
    { value: 'grossProfit', label: 'Gross Profit', format: 'currency', tooltip: 'Sum of all orders adjusted list price minus the buy price' },
    { value: 'partsOrdered', label: 'Parts Ordered', format: 'number', tooltip: 'Total number of parts ordered' },
    { value: 'avgListPrice', label: 'Avg. List Price', format: 'currency', tooltip: 'Total parts volume divided by the number of parts ordered' },
    { value: 'medianListPrice', label: 'Median List Price', format: 'currency', tooltip: 'The median of the list prices of parts ordered' },
    { value: 'avgListPriceWoPM', label: 'Avg. List Price (w/o PM)', format: 'currency', tooltip: 'Average list price using the base selected list price before price matching adjustments' },
    { value: 'medianListPriceWoPM', label: 'Median List Price (w/o PM)', format: 'currency', tooltip: 'Median list price using the base selected list price before price matching adjustments' },
    { value: 'pctOemVolume', label: '% OEM (Volume)', format: 'percent', tooltip: 'Percentage of order volume from OEM parts' },
    { value: 'pctOemCount', label: '% OEM (Count)', format: 'percent', tooltip: 'Percentage of order count from OEM parts' },
    { value: 'pctLkqVolume', label: '% LKQ (Volume)', format: 'percent', tooltip: 'Percentage of order volume from LKQ parts' },
    { value: 'pctLkqCount', label: '% LKQ (Count)', format: 'percent', tooltip: 'Percentage of order count from LKQ parts' },
    { value: 'pctAftermarketVolume', label: '% Aftermarket (Volume)', format: 'percent', tooltip: 'Percentage of order volume from aftermarket parts' },
    { value: 'pctAftermarketCount', label: '% Aftermarket (Count)', format: 'percent', tooltip: 'Percentage of order count from aftermarket parts' },
];

const GROSS_MARGIN_METRICS: TimeseriesMetricOption[] = [
    { value: 'avgGrossMarginPct', label: 'Avg. Gross Margin', format: 'percent', tooltip: 'The sum of the adjusted list price minus the buy price divided by the adjusted list price' },
    { value: 'medianGrossMarginPct', label: 'Median Gross Margin', format: 'percent', tooltip: 'The median of the per-order gross margins (adjusted list minus buy, divided by adjusted list)' },
    { value: 'avgGrossMarginPctWoPM', label: 'Avg. Gross Margin (w/o PM)', format: 'percent', tooltip: 'Gross margins without list price adjustments for price matching' },
    { value: 'medianGrossMarginPctWoPM', label: 'Median Gross Margin (w/o PM)', format: 'percent', tooltip: 'The median of the per-order gross margins without list price adjustments for price matching' },
    { value: 'grossMarginLossFromPM', label: 'Gross Margin Lost from PM', format: 'percent', tooltip: 'The difference in the gross margin with and without list price adjustments from price matching' },
    { value: 'partListPricePctOfOE', label: 'Part List Price % of OE', format: 'percent', tooltip: 'Adjusted list price divided by the original list price' },
    { value: 'missedOpportunity', label: 'Missed Opportunity', format: 'currency', tooltip: 'Total list price minus the total insurance best for all parts' },
    { value: 'avgMissedOpportunity', label: 'Avg. Missed Opportunity', format: 'currency', tooltip: 'Total missed opportunity divided by orders with at least one eligible part' },
    { value: 'medianMissedOpportunity', label: 'Median Missed Opportunity', format: 'currency', tooltip: 'The median of the per-order missed opportunities, including eligible orders with $0' },
];

export type { WidgetSharedParams } from './useWidgetData';

export interface AuditingDrilldownPayload {
    locationIds?: string[];
    insuranceIds?: string[];
    makes?: string[];
    partTypes?: string[];
    vendors?: string[];
    orderIds?: string[];
    targetView?: 'orders' | 'parts';
}

const formatCurrency = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const formatPercent = (v: number) => `${v.toFixed(1)}%`;
const formatNumber = (v: number) => v.toLocaleString();

function getFormatter(format: string) {
    if (format === 'currency') return formatCurrency;
    if (format === 'percent') return formatPercent;
    return formatNumber;
}

function getIcon(format: string) {
    if (format === 'currency') return TbCurrencyDollar;
    if (format === 'percent') return TbPercentage;
    return TbBox;
}

const DOMAIN_ENTITY_LABELS: Record<string, string> = {
    location: 'Location',
    vendor: 'Vendor',
    insurance: 'Insurance',
    oem: 'OEM',
    partType: 'Part Type',
    order: 'Location',
};

const getRankingMaxItems = (config: WidgetConfig) => {
    if (config.showAllRows) return undefined;
    const gridSpan = config.gridSpan || 2;
    // Fetch capped at REPORTING_OVERVIEW_BUNDLE_LIMIT; UI shows 10 or that full-grid max.
    return gridSpan >= 4 ? REPORTING_OVERVIEW_BUNDLE_LIMIT : 10;
};

interface WidgetRendererProps {
    config: WidgetConfig;
    params: WidgetSharedParams;
    onAuditingDrilldown?: (payload: AuditingDrilldownPayload) => void;
}

const WidgetRenderer = memo(function WidgetRenderer({ config, params, onAuditingDrilldown }: WidgetRendererProps) {
    const widgetExcludeEvs = config.excludeEvs ?? false;
    const {
        summaryData,
        summaryLoading,
        summaryError,
        timeseriesData,
        timeseriesLoading,
        rankingsData,
        rankingsLoading,
        domainStatsItems,
        comparison,
        granularity,
        preset,
        dataStartDate,
        dataEndDate,
    } = useWidgetData(config, params);

    const getRankingItems = () =>
        domainStatsItems ?? (rankingsData ? (rankingsData as any)[config.dataKey || 'locations'] : undefined);

    const chartHeight = '100%';
    const chartLayoutKey = widgetChartLayoutKey(config);
    const canDrilldown = config.dataSource === 'rankings' && ['location', 'insurance', 'oem', 'partType', 'vendor'].includes(config.domain);
    const comparisonSupported = isReportingComparisonSupported(preset);
    const showComparisonBars = comparisonSupported && Boolean(config.showComparisonBars);

    const resolveDrilldownOrderIds = useDomainDrilldownOrderIds();
    // Mirror the location scoping the domain-stats query uses so resolved order IDs match the stat.
    const drilldownLocationId = config.domain === 'location'
        ? (params.isGm ? params.locationId : undefined)
        : params.locationId;

    const handleDomainDrilldown = async (item: { id?: string; name: string }) => {
        if (!onAuditingDrilldown) return;

        // Rollup stats no longer ship order IDs; resolve the exact set on demand (small live lookup).
        const orderIds = item.id
            ? await resolveDrilldownOrderIds({
                startDate: params.startDate,
                endDate: params.endDate,
                domain: config.domain as DomainDrilldownDomain,
                id: item.id,
                locationId: drilldownLocationId,
                excludeEvs: widgetExcludeEvs,
            })
            : undefined;

        switch (config.domain) {
            case 'location':
                if (!item.id) return;
                onAuditingDrilldown({ locationIds: [item.id], orderIds });
                break;
            case 'insurance':
                if (!item.id) return;
                onAuditingDrilldown({ insuranceIds: [item.id], orderIds });
                break;
            case 'oem':
                onAuditingDrilldown({ makes: [item.name], orderIds, targetView: 'orders' });
                break;
            case 'vendor':
                onAuditingDrilldown({ vendors: [item.name], orderIds, targetView: 'parts' });
                break;
            case 'partType':
                onAuditingDrilldown({ partTypes: [item.name], orderIds, targetView: 'parts' });
                break;
            default:
                break;
        }
    };

    // Key metric (summary stat)
    if (config.widgetType === 'keyMetric') {
        const isLoading = summaryLoading;
        const value = summaryData ? (summaryData as any)[config.metric] : undefined;
        const fmt = getFormatter(config.format);
        const IconComp = getIcon(config.format);

        const changeMap = summaryData?.change;
        const comparisonEnabled = config.comparison !== false;
        const changeDisplay = formatSummaryMetricChange(changeMap?.[config.metric], {
            suffix: config.format === 'percent' ? 'pp' : '%',
            isNegativeMetric: NEGATIVE_METRICS.includes(config.metric as any),
            comparisonEnabled,
        });
        const changePeriod = getSummaryComparisonLabel(preset);

        return (
            <Flex align="center">
                <Flex alignItems="center" justifyContent="center" w={9} h={9} borderRadius="full" bg="gray.100" mr={3} flexShrink={0}>
                    <Icon as={IconComp as React.ElementType} w={5} h={5} color="#0071E3" />
                </Flex>
                <Box flex="1" minW={0}>
                    <Skeleton isLoaded={!isLoading} h={isLoading ? '28px' : 'auto'} w={isLoading ? '100px' : 'auto'} borderRadius="md">
                        <Flex alignItems="baseline" gap={2}>
                            <Text fontSize="xl" fontWeight="bold" color="gray.900" lineHeight="tight">
                                {summaryError ? '—' : value !== undefined ? fmt(value) : '—'}
                            </Text>
                            {!isLoading && changeDisplay && (
                                <Text fontSize="xs" fontWeight="medium" color={summaryChangeColor(changeDisplay.tone)}>
                                    {summaryChangeArrow(changeDisplay.tone)}{changeDisplay.text}
                                    {changePeriod && (
                                        <Text as="span" color="gray.400" fontWeight="normal" ml={1}>
                                            {changePeriod}
                                        </Text>
                                    )}
                                </Text>
                            )}
                        </Flex>
                    </Skeleton>
                </Box>
            </Flex>
        );
    }

    // Bar chart / time series
    if (config.widgetType === 'timeSeries') {
        if (config.dataSource === 'rankings') {
            const items = getRankingItems();

            const allMetricOptions: RankingMetricOption[] = [
                ...VOLUME_METRIC_OPTIONS,
                ...MARGIN_METRIC_OPTIONS,
                ...(config.domain === 'partType' ? PART_TYPE_METRIC_OPTIONS : []),
            ];
            const seen = new Set<string>();
            const metricOptions = allMetricOptions.filter((m) => {
                if (seen.has(m.value)) return false;
                seen.add(m.value);
                return true;
            });
            if (!metricOptions.some((m) => m.value === config.metric)) {
                const fmt = config.format === 'currency' ? 'currency' : config.format === 'percent' ? 'percent' : 'number';
                const catalogEntry = [...TS_VOL_CATALOG, ...TS_MARGIN_CATALOG, ...RANKING_METRICS_CATALOG].find((m) => m.value === config.metric);
                metricOptions.push({
                    value: config.metric as any,
                    label: catalogEntry?.label || config.metric,
                    format: fmt,
                    tooltip: catalogEntry?.tooltip,
                });
            }

            return (
                <RankingBarChart
                    key={chartLayoutKey}
                    data={items}
                    isLoading={rankingsLoading}
                    title=""
                    metricOptions={metricOptions}
                    defaultMetric={config.metric}
                    layout="vertical"
                    height={chartHeight}
                    maxItems={getRankingMaxItems(config)}
                    hideControls
                    comparison={comparison}
                    preset={preset}
                    showComparisonBars={showComparisonBars}
                    initialSortOrder={config.sort || 'desc'}
                    labelFormatter={config.domain === 'partType' ? getPartTypeLabel : undefined}
                    controlledMetric={config.metric}
                    onItemClick={canDrilldown ? handleDomainDrilldown : undefined}
                    marketplaceBreakdown={Boolean(config.showMarketplaceBreakdown) && supportsMarketplaceBreakdown(config.domain, config.widgetType, config.metric)}
                />
            );
        }

        const marginMetricValues = new Set(GROSS_MARGIN_METRICS.map(option => option.value));
        const metricOptions = marginMetricValues.has(config.metric as OverviewMetric)
            ? GROSS_MARGIN_METRICS
            : ORDER_VOLUME_METRICS;

        return (
            <TimeseriesChart
                key={chartLayoutKey}
                data={timeseriesData?.data}
                isLoading={timeseriesLoading}
                defaultMetric={config.metric as OverviewMetric}
                title=""
                hideHeader
                showMetricSelector={false}
                height={chartHeight}
                granularity={granularity}
                periodStartDate={dataStartDate}
                periodEndDate={dataEndDate}
                comparison={comparison}
                preset={preset}
                metricOptions={metricOptions}
                showComparisonBars={showComparisonBars}
                marketplaceBreakdown={Boolean(config.showMarketplaceBreakdown) && supportsMarketplaceBreakdown(config.domain, config.widgetType, config.metric)}
            />
        );
    }

    // Top list (vertical ranking bars)
    if (config.widgetType === 'topList') {
        const items = getRankingItems();

        const allMetricOptions: RankingMetricOption[] = [
            ...VOLUME_METRIC_OPTIONS,
            ...MARGIN_METRIC_OPTIONS,
            ...(config.domain === 'partType' ? PART_TYPE_METRIC_OPTIONS : []),
        ];
        const seen = new Set<string>();
        const metricOptions = allMetricOptions.filter(m => {
            if (seen.has(m.value)) return false;
            seen.add(m.value);
            return true;
        });
        if (!metricOptions.some(m => m.value === config.metric)) {
            const fmt = config.format === 'currency' ? 'currency' : config.format === 'percent' ? 'percent' : 'number';
            const catalogEntry = [...TS_VOL_CATALOG, ...TS_MARGIN_CATALOG, ...RANKING_METRICS_CATALOG].find(m => m.value === config.metric);
            metricOptions.push({ value: config.metric as any, label: catalogEntry?.label || config.metric, format: fmt, tooltip: catalogEntry?.tooltip });
        }

        return (
            <RankingBarChart
                key={chartLayoutKey}
                data={items}
                isLoading={rankingsLoading}
                title=""
                metricOptions={metricOptions}
                defaultMetric={config.metric}
                layout="vertical"
                height={chartHeight}
                maxItems={getRankingMaxItems(config)}
                hideControls
                comparison={comparison}
                preset={preset}
                showComparisonBars={showComparisonBars}
                initialSortOrder={config.sort || 'desc'}
                labelFormatter={config.domain === 'partType' ? getPartTypeLabel : undefined}
                controlledMetric={config.metric}
                onItemClick={canDrilldown ? handleDomainDrilldown : undefined}
                marketplaceBreakdown={Boolean(config.showMarketplaceBreakdown) && supportsMarketplaceBreakdown(config.domain, config.widgetType, config.metric)}
            />
        );
    }

    if (config.widgetType === 'table') {
        const items = getRankingItems();

        const allMetricOptions: RankingMetricOption[] = [
            ...VOLUME_METRIC_OPTIONS,
            ...MARGIN_METRIC_OPTIONS,
            ...(config.domain === 'partType' ? PART_TYPE_METRIC_OPTIONS : []),
        ];
        const seen = new Set<string>();
        const metricOptions = allMetricOptions.filter((m) => {
            if (seen.has(m.value)) return false;
            seen.add(m.value);
            return true;
        });
        if (!metricOptions.some((m) => m.value === config.metric)) {
            const fmt = config.format === 'currency' ? 'currency' : config.format === 'percent' ? 'percent' : 'number';
            const catalogEntry = [...TS_VOL_CATALOG, ...TS_MARGIN_CATALOG, ...RANKING_METRICS_CATALOG].find((m) => m.value === config.metric);
            metricOptions.push({
                value: config.metric as any,
                label: catalogEntry?.label || config.metric,
                format: fmt,
                tooltip: catalogEntry?.tooltip,
            });
        }

        return (
            <RankingTableWidget
                data={items}
                isLoading={rankingsLoading}
                metricOptions={metricOptions}
                defaultMetric={config.metric}
                controlledMetric={config.metric}
                comparison={config.comparison ? comparison : undefined}
                preset={preset}
                entityLabel={DOMAIN_ENTITY_LABELS[config.domain] || 'Entity'}
                labelFormatter={config.domain === 'partType' ? getPartTypeLabel : undefined}
                maxItems={getRankingMaxItems(config)}
                initialSortOrder={config.sort || 'desc'}
                onItemClick={canDrilldown ? handleDomainDrilldown : undefined}
                marketplaceBreakdown={Boolean(config.showMarketplaceBreakdown) && supportsMarketplaceBreakdown(config.domain, config.widgetType, config.metric)}
            />
        );
    }

    // Pie chart
    if (config.widgetType === 'pieChart') {
        return (
            <PartTypePieChart
                key={chartLayoutKey}
                data={getRankingItems()}
                isLoading={rankingsLoading}
                hideHeader
                height="100%"
                metric={config.metric}
                labelFormatter={config.domain === 'partType' ? getPartTypeLabel : undefined}
                onItemClick={canDrilldown ? handleDomainDrilldown : undefined}
            />
        );
    }

    return <Text color="gray.400" fontSize="sm">Unsupported widget type</Text>;
});

export default WidgetRenderer;
